import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { isAxiosError } from 'axios';

import axios from '../../api/axios';
import { fetchHubs } from './hubs-slice';
import { fetchSensors } from './sensors-slice';
import type { ClientsState } from '../../types/store';
import type {
  AddContract,
  Contract,
  DeleteHubFromContract,
  DeleteSensorFromContract,
  EditContract,
  EditHubId,
  PostContractResultAdditionalData,
} from '../../types/contract';

const initialState: ClientsState = {
  contracts: [],
  status: 'idle',
  error: null,
};

const mergeUpdatedContract = (state: ClientsState, payloadData: Partial<Contract> | null) => {
  if (!payloadData) {
    return;
  }

  const index = state.contracts.findIndex(
    (contract) => contract.contract_id === payloadData.contract_id,
  );

  if (index === -1) {
    return;
  }

  state.contracts[index] = {
    ...state.contracts[index],
    ...payloadData,
  };
};

const postContractUpdate = async (
  data: Partial<Contract>,
  additionalData: PostContractResultAdditionalData = {},
): Promise<
  PostContractResultAdditionalData & {
    data: Contract | null;
    error?: unknown;
  }
> => {
  try {
    const response = await axios.post('contract', data);
    if (response.data.result) {
      const active =
        dayjs().valueOf() >= response.data.result.since &&
        dayjs().valueOf() <= response.data.result.until;
      return {
        data: {
          ...response.data.result,
          active: active,
        },
        ...additionalData,
      };
    }
    return { data: null, ...additionalData };
  } catch (error) {
    if (isAxiosError(error)) {
      console.error('Error updating contract:', error.response?.data);
      return { data: null, error: error.response?.data };
    }
    return { data: null, error };
  }
};

export const fetchContracts = createAsyncThunk('clients/fetchContracts', async () => {
  const limit = 1000;
  const [activeContracts, inactiveContracts] = await Promise.all([
    axios.get('contracts', { params: { type: 'active', limit } }),
    axios.get('contracts', { params: { type: 'inactive', limit } }),
  ]);
  const active = activeContracts.data;
  const inactive = inactiveContracts.data;

  const contracts = [...active, ...inactive].map((contract) => ({
    ...contract,
    active: dayjs().valueOf() >= contract.since && dayjs().valueOf() <= contract.until,
  }));

  return {
    contracts: contracts,
  };
});

export const addContract = createAsyncThunk(
  'clients/addContract',
  async ({ data }: { data: AddContract }, { rejectWithValue }) => {
    try {
      const response = await axios.post('contract', data);
      if (response.data.result) {
        return { data: response.data.result };
      }
      return { data: null };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const editContract = createAsyncThunk(
  'clients/editContract',
  async ({ data }: EditContract, { rejectWithValue }) => {
    const result = await postContractUpdate(data);
    if (!result.data) {
      return rejectWithValue(result.error);
    }
    return result;
  },
);

export const deleteHubFromContract = createAsyncThunk(
  'clients/deleteHubFromContract',
  async ({ data, hubId }: DeleteHubFromContract, { rejectWithValue }) => {
    const result = await postContractUpdate(data, { hubId });
    if (!result.data) {
      return rejectWithValue(result.error);
    }
    return result;
  },
);

export const addHubToContract = createAsyncThunk(
  'clients/addHubToContract',
  async ({ data }: EditContract, { dispatch, rejectWithValue }) => {
    const result = await postContractUpdate(data);
    if (!result.data) {
      return rejectWithValue(result.error);
    }
    dispatch(fetchHubs());
    return result;
  },
);

export const editHubId = createAsyncThunk(
  'clients/editHubId',
  async ({ data, previousHubId, newHubId }: EditHubId, { rejectWithValue }) => {
    const result = await postContractUpdate(data, { previousHubId, newHubId });
    if (!result.data) {
      return rejectWithValue(result.error);
    }
    return result;
  },
);

export const editSensor = createAsyncThunk(
  'clients/editSensor',
  async ({ data }: EditContract, { dispatch, rejectWithValue }) => {
    const result = await postContractUpdate(data);
    if (!result.data) {
      return rejectWithValue(result.error);
    }

    dispatch(fetchSensors());

    return result;
  },
);

export const deleteSensorFromContract = createAsyncThunk(
  'clients/deleteSensorFromContract',
  async ({ data, sensorId }: DeleteSensorFromContract, { rejectWithValue }) => {
    const result = await postContractUpdate(data, { sensorId });
    if (!result.data) {
      return rejectWithValue(result.error);
    }
    return result;
  },
);

export const addSensorToContract = createAsyncThunk(
  'clients/addSensorToContract',
  async ({ data }: EditContract, { dispatch, rejectWithValue }) => {
    const result = await postContractUpdate(data);
    if (!result.data) {
      return rejectWithValue(result.error);
    }
    dispatch(fetchSensors());

    return result;
  },
);

const clientsSlice = createSlice({
  name: 'clients',
  initialState: initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchContracts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.contracts = action.payload.contracts;
        console.debug('Fetched contracts:', state.contracts);
      })
      .addCase(addContract.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const data: Contract | null = action.payload.data;
        if (data !== null) {
          state.contracts.push(data);
          console.debug('Contract added:', data);
        }
      })
      .addMatcher(
        isAnyOf(
          editContract.fulfilled,
          deleteHubFromContract.fulfilled,
          addHubToContract.fulfilled,
          editHubId.fulfilled,
          deleteSensorFromContract.fulfilled,
          addSensorToContract.fulfilled,
          editSensor.fulfilled,
        ),
        (state, action) => {
          state.status = 'succeeded';
          mergeUpdatedContract(state, action.payload.data);
        },
      )
      .addMatcher(
        isAnyOf(
          addContract.pending,
          editContract.pending,
          deleteHubFromContract.pending,
          addHubToContract.pending,
          editHubId.pending,
          deleteSensorFromContract.pending,
          addSensorToContract.pending,
          editSensor.pending,
        ),
        (state) => {
          state.status = 'loading';
          state.error = null;
        },
      )
      .addMatcher(
        isAnyOf(
          addContract.rejected,
          editContract.rejected,
          deleteHubFromContract.rejected,
          addHubToContract.rejected,
          editHubId.rejected,
          deleteSensorFromContract.rejected,
          addSensorToContract.rejected,
          editSensor.rejected,
        ),
        (state, action) => {
          state.status = 'failed';
          state.error = action.error.message;
        },
      );
  },
});

export default clientsSlice.reducer;
